import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Download, FileText, Book, Code } from 'lucide-react'

interface DownloadItem {
  id: string
  title: string
  description: string
  version?: string
  size: string
  type: 'manual' | 'software' | 'documentation' | 'sdk'
  icon: React.ReactNode
  downloadUrl: string
}

const downloads: DownloadItem[] = [
  {
    id: '1',
    title: 'User Manual',
    description: 'Comprehensive guide for PulseWindow Portal',
    version: 'v2.0',
    size: '5.2 MB',
    type: 'manual',
    icon: <Book className="h-5 w-5" />,
    downloadUrl: '/downloads/user-manual.pdf'
  },
  {
    id: '2',
    title: 'Device Setup Guide',
    description: 'Step-by-step instructions for device configuration',
    version: 'v1.5',
    size: '2.8 MB',
    type: 'documentation',
    icon: <FileText className="h-5 w-5" />,
    downloadUrl: '/downloads/device-setup-guide.pdf'
  },
  {
    id: '3',
    title: 'API Documentation',
    description: 'Complete API reference and integration guide',
    version: 'v2.1',
    size: '3.5 MB',
    type: 'documentation',
    icon: <Code className="h-5 w-5" />,
    downloadUrl: '/downloads/api-documentation.pdf'
  },
  {
    id: '4',
    title: 'SDK Package',
    description: 'Software Development Kit for custom integrations',
    version: 'v1.3.0',
    size: '45.2 MB',
    type: 'sdk',
    icon: <Code className="h-5 w-5" />,
    downloadUrl: '/downloads/pulsewindow-sdk.zip'
  },
  {
    id: '5',
    title: 'Desktop Client',
    description: 'Desktop application for Windows and macOS',
    version: 'v2.0.5',
    size: '128 MB',
    type: 'software',
    icon: <Download className="h-5 w-5" />,
    downloadUrl: '/downloads/desktop-client.dmg'
  },
  {
    id: '6',
    title: 'Quick Start Guide',
    description: 'Get started with PulseWindow in 5 minutes',
    size: '1.2 MB',
    type: 'manual',
    icon: <FileText className="h-5 w-5" />,
    downloadUrl: '/downloads/quick-start.pdf'
  }
]

const getTypeLabel = (type: DownloadItem['type']) => {
  const labels = {
    manual: 'Manual',
    software: 'Software',
    documentation: 'Documentation',
    sdk: 'SDK'
  }
  return labels[type]
}

const getTypeColor = (type: DownloadItem['type']) => {
  const colors = {
    manual: 'bg-blue-100 text-blue-800 border-blue-300',
    software: 'bg-green-100 text-green-800 border-green-300',
    documentation: 'bg-purple-100 text-purple-800 border-purple-300',
    sdk: 'bg-orange-100 text-orange-800 border-orange-300'
  }
  return colors[type]
}

export const DownloadCenterPage = () => {
  const handleDownload = (item: DownloadItem) => {
    console.log('Downloading:', item.title)
    // Note: Download URLs point to /downloads/* which should be served by the backend
    // or configured in the web server. For now, this will attempt to open the URL.
    window.open(item.downloadUrl, '_blank')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Download Center</h1>
        <p className="text-muted-foreground">
          Access user manuals, software, and documentation
        </p>
      </div>

      <div className="grid gap-4">
        {downloads.map((item) => (
          <Card key={item.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-primary/10 p-3">
                    {item.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle>{item.title}</CardTitle>
                      {item.version && (
                        <Badge variant="outline" className="text-xs">
                          {item.version}
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="mt-1">
                      {item.description}
                    </CardDescription>
                  </div>
                </div>
                <Button onClick={() => handleDownload(item)}>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <Badge className={getTypeColor(item.type)}>
                  {getTypeLabel(item.type)}
                </Badge>
                <span>Size: {item.size}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-lg">Need Help?</CardTitle>
          <CardDescription>
            Can't find what you're looking for? Contact our support team for assistance.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <a href="/contact">Contact Support</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
